import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { useApiUrls } from "../useApiUrl";
import { http } from "../../utils/httpCommon";
import { toast } from "../../utils/snackbarHelper";
import { toastMessage } from "../../constants/strings";
import { shipmentSchema } from "../../services/prePlanningService.types";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { useInvalidateQueriesAfterPlanChange } from "./useInvalidateQueriesAfterPlanChange";

const shipmentSearchSchema = z.object({
  location: z.enum(["delivery_trap", "delivery_route", "unassigned_shipments"]),
  locationId: z.string(),
  shipmentId: z.number(),
});

const addToPlanResponseSchema = z
  .object({
    failedProcessedShipmentCount: z.number(),
    failedShipmentIds: z.array(z.number()),
    failedShipments: z.array(shipmentSchema),
    processedShipmentCount: z.number(),
    processedShipmentIds: z.array(z.number()),
    shipmentCountSubmitted: z.number(),
    // cSpell:ignore Consigee
    // TODO: Fix this on the API Response
    shipmentIdsWithMissingConsigeeInfo: z.array(z.number()).optional(),
    successfullyProcessedShipmentCount: z.number(),
    unprocessedShipmentCount: z.number(),
    unprocessedShipmentSearchResults: z.array(shipmentSearchSchema),
  })
  .transform((input) => ({
    ...input,
    shipmentIdsWithMissingConsigneeInfo:
      input.shipmentIdsWithMissingConsigeeInfo,
  }));

export type AddToPlanResponse = z.infer<typeof addToPlanResponseSchema>;

// trips and traps args used to invalidate shipment details queries
type UseAddToPlanArgs = {
  date: number;
  sicId: number;
  shipmentIds: number[];
  onSuccess?: (
    affectedShipmentsCount: number,
    unprocessedShipmentCount: number
  ) => void;
};

export const useAddToPlan = ({
  date,
  sicId,
  shipmentIds,
  onSuccess,
}: UseAddToPlanArgs) => {
  const invalidateQueriesAfterPlanChange =
    useInvalidateQueriesAfterPlanChange();
  const hookUrls = useApiUrls();
  const { addToPlan } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );
  return useMutation({
    mutationFn: async () => {
      const url = addToPlan(sicId, date);
      const { status, data } = await http.post(url, {
        shipmentIds,
      });
      const parsedData = isSafeParseEnabled
        ? safeParsePartial(addToPlanResponseSchema, data, "useAddToPlanData")
        : addToPlanResponseSchema.parse(data);
      return { status, data: parsedData };
    },
    onSuccess: (response) => {
      onSuccess?.(
        response.data.successfullyProcessedShipmentCount,
        response.data.unprocessedShipmentCount
      );
    },
    onError: (e) => {
      // Ignoring 400 error, it will be implemented post MVP
      if (axios.isAxiosError(e) && e.response?.status === 400) {
        onSuccess?.(0, e.response.data.unprocessedShipmentCount);
        return;
      }
      toast(toastMessage.inbound.addToPlan.failed, {
        variant: "error",
      });
    },
    onSettled: () => {
      invalidateQueriesAfterPlanChange(date, sicId);
    },
  });
};
